/**
 * Claude Cowork guide — locale-keyed copy (en / ms / zh).
 *
 * EVERY user-visible string in ClaudeCowork.tsx lives here, keyed by locale, so
 * the component renders fully in Bahasa Malaysia and Simplified Chinese on the
 * localized /ms and /zh routes while staying byte-identical in English.
 *
 * Brand/product names (Claude, Anthropic, Claude Cowork, Claude Code, Claude
 * Agent SDK, MCP, Model Context Protocol, Anchor Sprint, Anthropic Claude
 * Partner Network, Compliance API), plan names (Free, Pro, Max, Team,
 * Enterprise), numbers, prices, dates, and technical tokens are never
 * translated.
 */
import type { Locale } from "@/lib/i18n";

type Tool = "chat" | "cowork" | "code";

export type Content = {
  /**
   * Dated correction banner. Added 2026-09-22 after Anthropic merged Cowork and
   * chat into one Claude on 16 September 2026 [CW1], which contradicted this
   * guide's central premise ("a working session, not a chat"). The page holds
   * avg position 8.3 on 281 monthly impressions [CW9], so it is corrected in
   * place rather than removed. Sourced in
   * docs/research/claude-cowork-merged-into-claude.md.
   */
  mergeNotice: { heading: string; body: string; linkLabel: string };
  hero: {
    breadcrumbHome: string;
    breadcrumbCurrent: string;
    eyebrow: string;
    title: string;
    // Intro paragraph is split so the "Anthropic Claude Partner Network" link
    // can stay a link with untranslated brand text.
    introBefore: string;
    introLinkLabel: string;
    introAfter: string;
    ctaPrimary: string;
    ctaSecondary: string;
    disclaimerBefore: string;
    disclaimerLinkLabel: string;
    disclaimerAfter: string;
    imageAlt: string;
  };
  whatIs: {
    eyebrow: string;
    // Paragraph 1 with an emphasized lead span.
    p1Emphasis: string;
    p1Rest: string;
    // Paragraph 2 with three emphasized spans.
    p2a: string;
    p2Emphasis1: string;
    p2b: string;
    p2Emphasis2: string;
    p2c: string;
    cards: ReadonlyArray<{ label: string; note: string }>;
  };
  video: {
    eyebrow: string;
    title: string;
    body: string;
    iframeTitle: string;
    playAriaLabel: string;
    facadeImgAlt: string;
    creditBefore: string;
    creditLinkLabel: string;
    creditAfter: string;
  };
  problemsSection: {
    eyebrow: string;
  };
  problems: ReadonlyArray<{ title: string; body: string }>;
  compare: {
    eyebrow: string;
    title: string;
    body: string;
    showAll: string;
    dimensionHeader: string;
    taskRouterLabel: string;
  };
  tools: ReadonlyArray<{ id: Tool; label: string; bestFor: string }>;
  comparison: ReadonlyArray<{
    dimension: string;
    chat: string;
    cowork: string;
    code: string;
  }>;
  taskRoutes: ReadonlyArray<{ task: string; tool: Tool }>;
  howTo: {
    eyebrow: string;
    steps: ReadonlyArray<{ title: string; body: string }>;
  };
  useCasesSection: {
    eyebrow: string;
    title: string;
  };
  useCases: ReadonlyArray<{
    tag: string;
    title: string;
    body: string;
    prompt: string;
  }>;
  pricing: {
    eyebrow: string;
    p1a: string;
    p1Emphasis1: string;
    p1b: string;
    p1Emphasis2: string;
    p1c: string;
    p2a: string;
    p2LinkLabel1: string;
    p2b: string;
    p2LinkLabel2: string;
    p2c: string;
  };
  limitationsSection: {
    eyebrow: string;
    title: string;
    body: string;
  };
  limitations: ReadonlyArray<string>;
  conversion: {
    eyebrow: string;
    title: string;
    body: string;
    training: {
      eyebrow: string;
      title: string;
      body: string;
      points: ReadonlyArray<string>;
      cta: string;
    };
    agents: {
      eyebrow: string;
      title: string;
      body: string;
      points: ReadonlyArray<string>;
      ctaPrimary: string;
      ctaSecondary: string;
    };
  };
  faqSection: {
    eyebrow: string;
  };
  faqs: ReadonlyArray<{ question: string; answer: string }>;
  keepReading: {
    eyebrow: string;
    links: ReadonlyArray<string>;
  };
  disclaimer: {
    label: string;
    before: string;
    linkLabel: string;
    after: string;
  };
  consultation: {
    title: string;
    description: string;
    buttonText: string;
  };
};

const en: Content = {
  mergeNotice: {
    heading: "Updated 22 September 2026 — Cowork and chat are now one Claude",
    body:
      "On 16 September 2026 Anthropic merged Claude Cowork and Claude chat into a single Claude. You no longer pick which one a task belongs to: you bring a quick question or hand over a whole report in the same place, and Claude can keep working after you close your laptop. It is rolling out to Pro and Max plans first across web, desktop and mobile, with Team and Free to follow — there is nothing to switch on, and if you had work in Cowork, it is where you left it. Cowork was not discontinued and Anthropic still uses the name. Read the guide below as a description of that way of working; where it says Cowork is a separate place, that describes the arrangement before this merge.",
    linkLabel: "Anthropic's announcement",
  },
  hero: {
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Claude Cowork",
    eyebrow: "The complete guide",
    title: "What is Claude Cowork — and how to use it",
    introBefore:
      "Claude Cowork is Anthropic's agentic AI for knowledge work: it runs in the Claude desktop app and completes whole tasks across your local files, folders, and apps. This guide covers what it is, the problems it solves, how to use it, and — honestly — where it stops and a custom AI agent begins. Written for Malaysian teams by a ",
    introLinkLabel: "Claude partner in Malaysia",
    introAfter: ", Certified in the Claude Partner Network.",
    ctaPrimary: "Automate beyond Cowork — talk to us",
    ctaSecondary: "Cowork vs Claude Code vs chat",
    disclaimerBefore:
      "An independent, compiled reference — last verified September 2026. Cowork changes fast; for the latest, always check ",
    disclaimerLinkLabel: "Anthropic's official site",
    disclaimerAfter: ".",
    imageAlt:
      "Abstract editorial illustration: a tangle of ink lines on the left resolving into calm, ordered parallel lines on the right, with scattered squares becoming an aligned row — Claude Cowork bringing order to scattered work.",
  },
  whatIs: {
    eyebrow: "What is Claude Cowork?",
    p1Emphasis: "Cowork is a way of working, not a separate app.",
    p1Rest:
      " A chat message gets you an answer; handing over a task gets you the work. You describe the outcome you want, Claude plans the steps and executes them across your files, and you steer along the way. Since 16 September 2026 both happen inside the same Claude.",
    p2a: "It lives inside the ",
    p2Emphasis1: "Claude desktop app",
    p2b:
      " — no terminal, no setup scripts — and uses the same agentic architecture that powers Claude Code. It runs in an ",
    p2Emphasis2: "isolated virtual machine on your own computer",
    p2c:
      " with controlled file and network access, so it can act on real documents while your data stays local.",
    cards: [
      { label: "Runs on your desktop", note: "In the Claude app, no terminal" },
      { label: "Files stay local", note: "Sandboxed VM, folders you grant" },
      { label: "Finishes whole tasks", note: "Plans, executes, you steer" },
    ],
  },
  video: {
    eyebrow: "See it in action",
    title: "Anthropic introduces Cowork",
    body:
      "Rather than take our word for it, watch the team behind Cowork demonstrate it. This is Anthropic's official introduction — the fastest way to see how a working session actually feels.",
    iframeTitle: "Anthropic — The Future of AI at Work: Introducing Cowork",
    playAriaLabel: "Play video: Anthropic introduces Claude Cowork",
    facadeImgAlt: "Anthropic's official introduction to Claude Cowork",
    creditBefore:
      "Video by Anthropic, embedded from YouTube. ",
    creditLinkLabel: "Watch on YouTube",
    creditAfter: ".",
  },
  problemsSection: {
    eyebrow: "What problem does it solve?",
  },
  problems: [
    {
      title: "Work too big for a chat window",
      body:
        "A chat handles one question at a time. Cowork reads dozens of files, produces large outputs, and keeps working through a multi-step task without you re-pasting context.",
    },
    {
      title: "Scattered files and manual busywork",
      body:
        "Sorting, renaming, standardising, and extracting data across document sets is slow and error-prone by hand. Cowork does it across whole folders in one working session.",
    },
    {
      title: "Repeatable workflows nobody has time for",
      body:
        "The recurring report, the weekly clean-up, the data pull. Cowork can run the same multi-step routine again on demand, freeing your team for judgement work.",
    },
  ],
  compare: {
    eyebrow: "Cowork vs Claude Code vs chat",
    title: "Which one should you use?",
    body:
      "All three share the same Claude intelligence — they differ in where they run and what they produce. Tap a tool to highlight it and see which everyday tasks it fits.",
    showAll: "Show all",
    dimensionHeader: "Dimension",
    taskRouterLabel: "Match the task to the tool",
  },
  tools: [
    { id: "chat", label: "Claude chat", bestFor: "Quick questions, drafting, and thinking out loud in a conversation." },
    { id: "cowork", label: "Claude Cowork", bestFor: "Finishing knowledge work across your local files, folders, and apps." },
    { id: "code", label: "Claude Code", bestFor: "Building software in a repository, from the terminal." },
  ],
  comparison: [
    { dimension: "Best for", chat: "Quick answers & drafting", cowork: "Everyday knowledge work", code: "Writing & shipping software" },
    { dimension: "Where it runs", chat: "Web, mobile & desktop", cowork: "Desktop app (no terminal)", code: "Terminal / IDE" },
    { dimension: "Works with your files", chat: "Uploads only", cowork: "Local folders you grant", code: "Your code repository" },
    { dimension: "How it works", chat: "One turn at a time", cowork: "Plans & runs a whole task", code: "Plans & runs a whole task" },
    { dimension: "Typical output", chat: "A reply", cowork: "Reports, sorted files, data", code: "Code, scripts, fixes" },
    { dimension: "Who it's for", chat: "Everyone", cowork: "Ops, finance, legal, research", code: "Developers" },
    { dimension: "Isolation / safety", chat: "N/A", cowork: "Sandboxed VM, granted folders", code: "Runs in your environment" },
  ],
  taskRoutes: [
    { task: "Summarise a folder of 40 meeting notes into one brief", tool: "cowork" },
    { task: "Ask a one-off question about a contract clause", tool: "chat" },
    { task: "Reconcile figures across a dozen spreadsheets", tool: "cowork" },
    { task: "Draft a quick email or social post", tool: "chat" },
    { task: "Build an internal automation script", tool: "code" },
    { task: "Rename and sort 200 files by their content", tool: "cowork" },
    { task: "Debug a failing test in your codebase", tool: "code" },
    { task: "Brainstorm ideas for a campaign", tool: "chat" },
    { task: "Prepare a monthly ops report from raw exports", tool: "cowork" },
  ],
  howTo: {
    eyebrow: "How to use Claude Cowork",
    steps: [
      {
        title: "Install the Claude desktop app",
        body:
          "Download Claude Desktop from claude.com/download and sign in. Cowork needs a paid plan (Pro, Max, Team, or Enterprise) — it is not on Free — and a supported desktop: Mac, Windows, or Linux (beta). The app runs a quick readiness check to confirm your computer can run it.",
      },
      {
        title: "Open Cowork and grant folder access",
        body:
          "Select the Cowork tab in the desktop app, then add the specific folders you want Claude to work in via Settings. It only ever touches folders you explicitly grant, inside an isolated virtual machine on your computer.",
      },
      {
        title: "Describe the task, then review the plan",
        body:
          "Tell Claude the outcome you want — 'summarise every file in this folder into one brief with owners and dates' — not step-by-step instructions. Claude proposes an approach; check it before it runs, especially on important folders.",
      },
      {
        title: "Let it run, and steer as it goes",
        body:
          "Cowork executes across files and steps while you watch and correct. When it finishes, review the output the same way you would a junior colleague's — it is fast and capable, but you own the final check.",
      },
    ],
  },
  useCasesSection: {
    eyebrow: "Real use cases for Malaysian teams",
    title: "What to hand Cowork on Monday morning",
  },
  useCases: [
    {
      tag: "Operations",
      title: "Turn raw exports into a monthly report",
      body:
        "Point Cowork at a folder of CSV exports and last month's report template; it reconciles the numbers and drafts this month's version.",
      prompt:
        "“Read every CSV in /ops-exports, match the format of last-month-report.docx, and draft this month's operations report with variances flagged.”",
    },
    {
      tag: "Finance",
      title: "Reconcile invoices and flag mismatches",
      body:
        "Hand it a folder of invoices and a ledger; it extracts totals, matches them, and lists what doesn't reconcile — with references.",
      prompt:
        "“Compare the invoices in /invoices against ledger.xlsx and produce a list of mismatches with invoice numbers and amounts.”",
    },
    {
      tag: "Legal & admin",
      title: "Summarise a document set for review",
      body:
        "Give it a directory of contracts; it produces a structured summary — parties, key dates, obligations — so your team reviews faster.",
      prompt:
        "“Summarise each contract in /vendor-agreements: parties, renewal date, notice period, and any auto-renewal clause, as a table.”",
    },
    {
      tag: "Marketing & research",
      title: "Synthesise research into one brief",
      body:
        "Feed it notes, PDFs, and transcripts; it distils the themes into a single brief with sources you can act on.",
      prompt:
        "“Read everything in /market-research and write a one-page brief on the top three themes, citing the source file for each point.”",
    },
  ],
  pricing: {
    eyebrow: "Plans, pricing & usage",
    p1a: "Cowork is ",
    p1Emphasis1: "included on every paid Claude plan",
    p1b:
      " — Pro, Max, Team, and Enterprise — but not on Free. Importantly, Pro is not a cut-down version: you get the full Cowork feature set. What changes between tiers is ",
    p1Emphasis2: "how much you can use it",
    p1c: " before you hit limits.",
    p2a:
      "Because Cowork runs long, multi-step tasks, it consumes usage far faster than ordinary chats. Pro is genuinely enough until you start running Cowork daily or on a schedule; once you do, Max 5× is the realistic floor, and Max 20× is for keeping it working all day. For the full plan-by-plan breakdown, see our ",
    p2LinkLabel1: "Claude plans comparison for Malaysia",
    p2b: ", and for privacy and data-handling across plans, our ",
    p2LinkLabel2: "Claude privacy & compliance guide",
    p2c: ".",
  },
  limitationsSection: {
    eyebrow: "Honest limitations",
    title: "What Claude Cowork can't do (yet)",
    body:
      "Cowork is powerful, but knowing its edges is what makes it useful — and it points to where you need something more.",
  },
  limitations: [
    "Single-user and desktop-only: no web, no mobile, and no shared sessions. It helps one person finish their own work.",
    "Session-based memory: memory works within projects, but Cowork does not retain memory across standalone sessions.",
    "The desktop app must stay open: if you close Claude Desktop, any running task stops.",
    "Usage-heavy: long, multi-step tasks consume plan usage far faster than ordinary chats, so heavy use can hit your limits quickly.",
    "Connector reliability varies: integrations with tools like Gmail or Google Drive can be inconsistent for now.",
    "A governance gap for regulated teams: Cowork activity is not captured in Anthropic's Compliance API at this time.",
    "Paid plan + internet required: Cowork is not on Free, and it needs an active connection throughout a session.",
    "You own the review: it asks approval before permanently deleting files, but because it acts on real documents, always check its plan first.",
  ],
  conversion: {
    eyebrow: "Where to from here",
    title: "Two ways we take your team further with Claude",
    body:
      "Whether your people just need to get fluent in Cowork, or you need automation Cowork can't do, that's our work as a firm Certified in the Claude Partner Network.",
    training: {
      eyebrow: "Custom training",
      title: "Custom Cowork training for teams in Malaysia and Singapore",
      body:
        "A public Cowork course teaches a fixed curriculum on someone else's examples. We run private sessions on your actual workflows — prompt patterns your people will reuse on Monday, and safe-usage guidance scoped to PDPA or PDPC as they apply to you.",
      points: [
        "Private sessions built on your workflows, not a public class",
        "Delivered across Malaysia and Singapore, on site or remote",
        "Safe-usage guidance scoped to PDPA or PDPC",
      ],
      cta: "Talk to us about custom training",
    },
    agents: {
      eyebrow: "Where Cowork stops",
      title: "Build a custom Claude agent",
      body:
        "Cowork is single-user and disconnected from your systems. For automation that runs across a whole team and plugs into your CRM, ERP, or database, we design and build custom Claude agents with the Agent SDK and MCP.",
      points: [
        "Runs across your whole team, always on",
        "Connects to your business systems via MCP",
        "Does the multi-step work Cowork can't",
      ],
      ctaPrimary: "Scope a custom agent",
      ctaSecondary: "Our AI Solutions",
    },
  },
  faqSection: {
    eyebrow: "Frequently asked questions",
  },
  faqs: [
    {
      question: "What is Claude Cowork?",
      answer:
        "Claude Cowork is Anthropic's agentic AI for knowledge work. It runs inside the Claude desktop app and works directly with your local files, folders, and applications. Since 16 September 2026 Cowork and chat are one Claude, so this is a way of working rather than a separate app: you describe a task, Claude plans and executes it across multiple files and steps, and you steer along the way. It uses the same agentic architecture that powers Claude Code, but without needing the terminal.",
    },
    {
      question: "Is Claude Cowork free, and which plan do I need?",
      answer:
        "Cowork is included on every paid Claude plan — Pro, Max, Team, and Enterprise — but not on the Free plan. Higher tiers mainly raise how much you can use it before hitting limits, so if you run Cowork daily or on a schedule, expect to need a higher tier such as Max. Verify current plans and pricing at claude.com/pricing.",
    },
    {
      question: "Does Claude Cowork send my files to the cloud?",
      answer:
        "Your files stay on your machine. Cowork runs in an isolated virtual machine on your own computer with controlled file and network access, and it only touches the folders you explicitly grant it. Your documents and data are not uploaded for training or cloud storage. An internet connection is still required because the model reasoning runs on Anthropic's servers, but your local files are not sent there wholesale.",
    },
    {
      question: "What is the difference between Claude Cowork and Claude Code?",
      answer:
        "Both use the same underlying agent, but they target different work. Claude Code is for developers: it runs in the terminal, works inside a code repository, and its output is software. Claude Cowork is for knowledge workers: it runs in the desktop app with no terminal, works across documents, spreadsheets, and everyday apps, and its output is finished work — reports, organised files, extracted data. Reach for Code when the output is software; reach for Cowork when the output is knowledge work.",
    },
    {
      question: "What can Claude Cowork actually do?",
      answer:
        "Cowork is built for tasks too big for a chat window. Typical jobs: reading dozens of files in a folder and producing a structured summary; extracting and reconciling data across many documents; preparing reports and briefs; sorting and renaming files by their content; standardising document sets; and running repeatable, multi-step workflows across your apps. It handles the whole task rather than answering one question at a time.",
    },
    {
      question: "What are the limitations of Claude Cowork?",
      answer:
        "Cowork is single-user and desktop-only (no web or mobile), and it needs a paid plan plus an internet connection. Memory works within projects, but it does not retain memory across standalone sessions, and the desktop app must stay open — closing it stops a running task. Long, multi-step tasks consume plan usage much faster than ordinary chats. External connectors (such as Gmail or Google Drive) can be inconsistent, and — worth noting for regulated teams — Cowork activity is not captured in Anthropic's Compliance API at this time. Because it acts on real files, review its plan before letting it run on important folders.",
    },
    {
      question: "Does Claude Cowork run on Windows, or only Mac?",
      answer:
        "Cowork runs through the Claude desktop app on macOS, Windows, and Linux (beta) — it is not available on the web or on mobile. Rather than publishing fixed hardware specs, Anthropic's app runs a quick readiness check to confirm your computer can run it. Because availability and system requirements change quickly for a new product, confirm the current list on Anthropic's Help Center before you plan a rollout.",
    },
    {
      question: "Cowork is single-user — how do we automate work across a whole team?",
      answer:
        "This is Cowork's boundary, and it is an important one. Cowork is a personal desktop agent: it helps an individual finish their own knowledge work. It does not run team-wide automations, connect to your CRM or ERP, or operate as a shared always-on service. When you need automation that spans a whole team and plugs into your business systems, that is a custom Claude agent — built with the Claude Agent SDK and the Model Context Protocol (MCP). Anchor Sprint, Certified in the Claude Partner Network, builds exactly that, and can also train your team to get more out of Cowork itself.",
    },
  ],
  keepReading: {
    eyebrow: "Keep reading",
    links: [
      "Compare every Claude plan (Free to Enterprise)",
      "Claude privacy & compliance",
      "Our Anthropic Claude partnership",
      "Custom AI agents we build",
    ],
  },
  disclaimer: {
    label: "Disclaimer",
    before:
      "We compiled this guide from Anthropic's official product page and Help Center to give you one clear, unified reference for Claude Cowork. Cowork is new and changes fast, so despite our best efforts to keep this accurate (last verified September 2026), some details may be out of date. For the latest and authoritative information, always refer to ",
    linkLabel: "Anthropic's official website",
    after:
      " — nothing here replaces Anthropic's own documentation. Anchor Sprint builds custom Claude agents and provides enablement, and is not a reseller of Claude. Claude and Anthropic are trademarks of Anthropic, PBC. Anchor Sprint is Certified in the Claude Partner Network; this does not imply endorsement of our services.",
  },
  consultation: {
    title: "Cowork got you started — ready to automate the rest?",
    description:
      "Tell us the repetitive, multi-step work your team does across its systems. We'll show you what a custom Claude agent can take off their plate, and help your people get more from Cowork along the way.",
    buttonText: "Talk to Our Team",
  },
};

const ms: Content = {
  mergeNotice: {
    heading: "Dikemas kini 22 September 2026 — Cowork dan chat kini satu Claude",
    body:
      "Pada 16 September 2026, Anthropic menggabungkan Claude Cowork dan Claude chat menjadi satu Claude. Anda tidak perlu lagi memilih tempat untuk sesuatu tugas: bawa soalan pantas atau serahkan laporan penuh di tempat yang sama, dan Claude boleh terus bekerja selepas anda menutup komputer. Ia sedang dilancarkan kepada pelan Pro dan Max dahulu merentasi web, desktop dan mudah alih, diikuti Team dan Free — tiada apa-apa untuk dihidupkan, dan jika anda ada kerja dalam Cowork, ia masih di tempat anda tinggalkan. Cowork tidak dihentikan dan Anthropic masih menggunakan nama itu. Baca panduan di bawah sebagai penerangan tentang cara kerja tersebut; di mana ia menyebut Cowork sebagai tempat berasingan, itu menggambarkan keadaan sebelum penggabungan ini.",
    linkLabel: "Pengumuman Anthropic",
  },
  hero: {
    breadcrumbHome: "Laman Utama",
    breadcrumbCurrent: "Claude Cowork",
    eyebrow: "Panduan lengkap",
    title: "Apa itu Claude Cowork — dan cara menggunakannya",
    introBefore:
      "Claude Cowork ialah AI agentik Anthropic untuk kerja pengetahuan: ia berjalan dalam aplikasi desktop Claude dan menyelesaikan tugas secara menyeluruh merentasi fail, folder, dan aplikasi tempatan anda. Panduan ini merangkumi apa itu Cowork, masalah yang diselesaikannya, cara menggunakannya, dan — secara jujur — di mana ia berhenti dan ejen AI tersuai bermula. Ditulis untuk pasukan Malaysia oleh ",
    introLinkLabel: "rakan kongsi Claude di Malaysia",
    introAfter: ".",
    ctaPrimary: "Automasikan melangkaui Cowork — hubungi kami",
    ctaSecondary: "Cowork vs Claude Code vs chat",
    disclaimerBefore:
      "Rujukan bebas yang disusun — disahkan kali terakhir pada September 2026. Cowork berubah dengan pantas; untuk maklumat terkini, sentiasa semak ",
    disclaimerLinkLabel: "laman rasmi Anthropic",
    disclaimerAfter: ".",
    imageAlt:
      "Ilustrasi editorial abstrak: kekusutan garisan dakwat di sebelah kiri bertukar menjadi garisan selari yang tenang dan teratur di sebelah kanan, dengan segi empat yang berselerak menjadi barisan yang sejajar — Claude Cowork membawa keteraturan kepada kerja yang berselerak.",
  },
  whatIs: {
    eyebrow: "Apa itu Claude Cowork?",
    p1Emphasis: "Cowork ialah cara bekerja, bukan aplikasi berasingan.",
    p1Rest:
      " Sementara Claude chat menjawab satu mesej pada satu masa, Cowork mengambil alih tugas secara menyeluruh: anda menerangkan hasil yang anda mahukan, Claude merancang langkah-langkahnya dan melaksanakannya merentasi fail anda, dan anda mengemudinya sepanjang perjalanan.",
    p2a: "Ia berada dalam ",
    p2Emphasis1: "aplikasi desktop Claude",
    p2b:
      " — tanpa terminal, tanpa skrip persediaan — dan menggunakan seni bina agentik yang sama yang menggerakkan Claude Code. Ia berjalan dalam ",
    p2Emphasis2: "mesin maya terpencil pada komputer anda sendiri",
    p2c:
      " dengan akses fail dan rangkaian yang terkawal, jadi ia boleh bertindak ke atas dokumen sebenar sementara data anda kekal setempat.",
    cards: [
      { label: "Berjalan pada desktop anda", note: "Dalam aplikasi Claude, tanpa terminal" },
      { label: "Fail kekal setempat", note: "Mesin maya sandbox, folder yang anda benarkan" },
      { label: "Menyelesaikan tugas sepenuhnya", note: "Merancang, melaksana, anda mengemudi" },
    ],
  },
  video: {
    eyebrow: "Lihat ia beraksi",
    title: "Anthropic memperkenalkan Cowork",
    body:
      "Daripada percaya kata-kata kami, tonton pasukan di sebalik Cowork mendemonstrasikannya. Ini ialah pengenalan rasmi Anthropic — cara terpantas untuk melihat rasa sesi kerja yang sebenar.",
    iframeTitle: "Anthropic — The Future of AI at Work: Introducing Cowork",
    playAriaLabel: "Main video: Anthropic memperkenalkan Claude Cowork",
    facadeImgAlt: "Pengenalan rasmi Anthropic kepada Claude Cowork",
    creditBefore:
      "Video oleh Anthropic, dibenamkan daripada YouTube. ",
    creditLinkLabel: "Tonton di YouTube",
    creditAfter: ".",
  },
  problemsSection: {
    eyebrow: "Apa masalah yang diselesaikannya?",
  },
  problems: [
    {
      title: "Kerja yang terlalu besar untuk tetingkap chat",
      body:
        "Chat mengendalikan satu soalan pada satu masa. Cowork membaca berpuluh-puluh fail, menghasilkan output yang besar, dan terus bekerja melalui tugas berbilang langkah tanpa anda perlu menampal semula konteks.",
    },
    {
      title: "Fail berselerak dan kerja manual yang membosankan",
      body:
        "Menyusun, menamakan semula, menyeragamkan, dan mengekstrak data merentasi set dokumen adalah perlahan dan mudah tersilap jika dilakukan secara manual. Cowork melakukannya merentasi folder secara keseluruhan dalam satu sesi kerja.",
    },
    {
      title: "Aliran kerja berulang yang tiada siapa ada masa",
      body:
        "Laporan berulang, pembersihan mingguan, tarikan data. Cowork boleh menjalankan rutin berbilang langkah yang sama semula atas permintaan, membebaskan pasukan anda untuk kerja yang memerlukan pertimbangan.",
    },
  ],
  compare: {
    eyebrow: "Cowork vs Claude Code vs chat",
    title: "Yang mana patut anda gunakan?",
    body:
      "Ketiga-tiganya berkongsi kecerdasan Claude yang sama — ia berbeza dari segi di mana ia berjalan dan apa yang dihasilkannya. Ketik satu alat untuk menyerlahkannya dan lihat tugas harian yang sesuai untuknya.",
    showAll: "Tunjuk semua",
    dimensionHeader: "Dimensi",
    taskRouterLabel: "Padankan tugas dengan alat",
  },
  tools: [
    { id: "chat", label: "Claude chat", bestFor: "Soalan pantas, penyediaan draf, dan berfikir secara terbuka dalam perbualan." },
    { id: "cowork", label: "Claude Cowork", bestFor: "Menyelesaikan kerja pengetahuan merentasi fail, folder, dan aplikasi tempatan anda." },
    { id: "code", label: "Claude Code", bestFor: "Membina perisian dalam repositori, dari terminal." },
  ],
  comparison: [
    { dimension: "Terbaik untuk", chat: "Jawapan pantas & draf", cowork: "Kerja pengetahuan harian", code: "Menulis & menghantar perisian" },
    { dimension: "Di mana ia berjalan", chat: "Web, mudah alih & desktop", cowork: "Aplikasi desktop (tanpa terminal)", code: "Terminal / IDE" },
    { dimension: "Berfungsi dengan fail anda", chat: "Muat naik sahaja", cowork: "Folder tempatan yang anda benarkan", code: "Repositori kod anda" },
    { dimension: "Cara ia berfungsi", chat: "Satu giliran pada satu masa", cowork: "Merancang & menjalankan tugas penuh", code: "Merancang & menjalankan tugas penuh" },
    { dimension: "Output tipikal", chat: "Satu balasan", cowork: "Laporan, fail tersusun, data", code: "Kod, skrip, pembaikan" },
    { dimension: "Untuk siapa", chat: "Semua orang", cowork: "Operasi, kewangan, perundangan, penyelidikan", code: "Pembangun" },
    { dimension: "Pemencilan / keselamatan", chat: "Tidak berkenaan", cowork: "Mesin maya sandbox, folder yang dibenarkan", code: "Berjalan dalam persekitaran anda" },
  ],
  taskRoutes: [
    { task: "Ringkaskan folder 40 nota mesyuarat menjadi satu ringkasan", tool: "cowork" },
    { task: "Tanya soalan sekali sahaja tentang klausa kontrak", tool: "chat" },
    { task: "Selaraskan angka merentasi belasan hamparan", tool: "cowork" },
    { task: "Sediakan draf e-mel atau siaran sosial ringkas", tool: "chat" },
    { task: "Bina skrip automasi dalaman", tool: "code" },
    { task: "Namakan semula dan susun 200 fail mengikut kandungannya", tool: "cowork" },
    { task: "Nyahpepijat ujian yang gagal dalam pangkalan kod anda", tool: "code" },
    { task: "Sumbang saran idea untuk kempen", tool: "chat" },
    { task: "Sediakan laporan operasi bulanan daripada eksport mentah", tool: "cowork" },
  ],
  howTo: {
    eyebrow: "Cara menggunakan Claude Cowork",
    steps: [
      {
        title: "Pasang aplikasi desktop Claude",
        body:
          "Muat turun Claude Desktop dari claude.com/download dan log masuk. Cowork memerlukan pelan berbayar (Pro, Max, Team, atau Enterprise) — ia tiada pada Free — dan desktop yang disokong: Mac, Windows, atau Linux (beta). Aplikasi ini menjalankan semakan kesediaan pantas untuk mengesahkan komputer anda mampu menjalankannya.",
      },
      {
        title: "Buka Cowork dan berikan akses folder",
        body:
          "Pilih tab Cowork dalam aplikasi desktop, kemudian tambah folder tertentu yang anda mahu Claude bekerja di dalamnya melalui Settings. Ia hanya menyentuh folder yang anda benarkan secara jelas, di dalam mesin maya terpencil pada komputer anda.",
      },
      {
        title: "Terangkan tugas, kemudian semak rancangan",
        body:
          "Beritahu Claude hasil yang anda mahukan — 'ringkaskan setiap fail dalam folder ini menjadi satu ringkasan dengan pemilik dan tarikh' — bukan arahan langkah demi langkah. Claude mencadangkan pendekatan; semaknya sebelum ia berjalan, terutamanya pada folder penting.",
      },
      {
        title: "Biarkan ia berjalan, dan kemudikan sepanjang perjalanan",
        body:
          "Cowork melaksanakan merentasi fail dan langkah sambil anda memerhati dan membetulkan. Apabila ia selesai, semak output seperti anda menyemak kerja rakan sekerja junior — ia pantas dan berkebolehan, tetapi anda yang memiliki semakan akhir.",
      },
    ],
  },
  useCasesSection: {
    eyebrow: "Kes penggunaan sebenar untuk pasukan Malaysia",
    title: "Apa yang boleh diserahkan kepada Cowork pada pagi Isnin",
  },
  useCases: [
    {
      tag: "Operasi",
      title: "Tukar eksport mentah menjadi laporan bulanan",
      body:
        "Arahkan Cowork ke folder eksport CSV dan templat laporan bulan lepas; ia menyelaraskan angka dan menyediakan draf versi bulan ini.",
      prompt:
        "“Baca setiap CSV dalam /ops-exports, padankan format last-month-report.docx, dan sediakan draf laporan operasi bulan ini dengan varians ditandakan.”",
    },
    {
      tag: "Kewangan",
      title: "Selaraskan invois dan tandakan ketidakpadanan",
      body:
        "Serahkan folder invois dan lejar kepadanya; ia mengekstrak jumlah, memadankannya, dan menyenaraikan apa yang tidak selaras — dengan rujukan.",
      prompt:
        "“Bandingkan invois dalam /invoices dengan ledger.xlsx dan hasilkan senarai ketidakpadanan dengan nombor invois dan jumlah.”",
    },
    {
      tag: "Perundangan & pentadbiran",
      title: "Ringkaskan set dokumen untuk semakan",
      body:
        "Berikan direktori kontrak kepadanya; ia menghasilkan ringkasan berstruktur — pihak, tarikh penting, kewajipan — supaya pasukan anda menyemak dengan lebih pantas.",
      prompt:
        "“Ringkaskan setiap kontrak dalam /vendor-agreements: pihak, tarikh pembaharuan, tempoh notis, dan sebarang klausa pembaharuan automatik, dalam bentuk jadual.”",
    },
    {
      tag: "Pemasaran & penyelidikan",
      title: "Sintesiskan penyelidikan menjadi satu ringkasan",
      body:
        "Berikan nota, PDF, dan transkrip kepadanya; ia menyaring tema menjadi satu ringkasan tunggal dengan sumber yang boleh anda gunakan.",
      prompt:
        "“Baca semua dalam /market-research dan tulis ringkasan satu muka surat tentang tiga tema teratas, memetik fail sumber untuk setiap perkara.”",
    },
  ],
  pricing: {
    eyebrow: "Pelan, harga & penggunaan",
    p1a: "Cowork ",
    p1Emphasis1: "disertakan pada setiap pelan Claude berbayar",
    p1b:
      " — Pro, Max, Team, dan Enterprise — tetapi tidak pada Free. Yang penting, Pro bukan versi yang dikurangkan: anda mendapat set ciri Cowork penuh. Apa yang berubah antara peringkat ialah ",
    p1Emphasis2: "berapa banyak anda boleh menggunakannya",
    p1c: " sebelum anda mencapai had.",
    p2a:
      "Kerana Cowork menjalankan tugas berbilang langkah yang panjang, ia menggunakan kuota jauh lebih pantas daripada chat biasa. Pro benar-benar mencukupi sehingga anda mula menjalankan Cowork setiap hari atau mengikut jadual; setelah anda melakukannya, Max 5× ialah tahap minimum yang realistik, dan Max 20× untuk memastikan ia berfungsi sepanjang hari. Untuk pecahan pelan demi pelan yang penuh, lihat ",
    p2LinkLabel1: "perbandingan pelan Claude untuk Malaysia",
    p2b: " kami, dan untuk privasi serta pengendalian data merentasi pelan, lihat ",
    p2LinkLabel2: "panduan privasi & pematuhan Claude",
    p2c: " kami.",
  },
  limitationsSection: {
    eyebrow: "Batasan yang jujur",
    title: "Apa yang Claude Cowork tidak boleh lakukan (buat masa ini)",
    body:
      "Cowork berkuasa, tetapi mengetahui batasannya adalah apa yang menjadikannya berguna — dan ia menunjukkan di mana anda memerlukan sesuatu yang lebih.",
  },
  limitations: [
    "Pengguna tunggal dan desktop sahaja: tiada web, tiada mudah alih, dan tiada sesi dikongsi. Ia membantu seorang menyelesaikan kerjanya sendiri.",
    "Ingatan berasaskan sesi: ingatan berfungsi dalam projek, tetapi Cowork tidak mengekalkan ingatan merentasi sesi tersendiri.",
    "Aplikasi desktop mesti kekal terbuka: jika anda menutup Claude Desktop, sebarang tugas yang sedang berjalan akan berhenti.",
    "Penggunaan intensif: tugas berbilang langkah yang panjang menggunakan kuota pelan jauh lebih pantas daripada chat biasa, jadi penggunaan berat boleh mencapai had anda dengan cepat.",
    "Kebolehpercayaan penyambung berbeza-beza: integrasi dengan alat seperti Gmail atau Google Drive boleh menjadi tidak konsisten buat masa ini.",
    "Jurang tadbir urus untuk pasukan yang dikawal selia: aktiviti Cowork tidak ditangkap dalam Compliance API Anthropic pada masa ini.",
    "Pelan berbayar + internet diperlukan: Cowork tiada pada Free, dan ia memerlukan sambungan aktif sepanjang sesi.",
    "Anda memiliki semakan: ia meminta kebenaran sebelum memadam fail secara kekal, tetapi kerana ia bertindak ke atas dokumen sebenar, sentiasa semak rancangannya dahulu.",
  ],
  conversion: {
    eyebrow: "Ke mana dari sini",
    title: "Dua cara kami membawa pasukan anda lebih jauh dengan Claude",
    body:
      "Sama ada pasukan anda hanya perlu mahir dalam Cowork, atau anda memerlukan automasi yang Cowork tidak boleh lakukan, itulah kerja kami sebagai firma yang diperakui dalam Claude Partner Network.",
    training: {
      eyebrow: "Latihan tersuai",
      title: "Latihan Cowork tersuai untuk pasukan di Malaysia dan Singapura",
      body:
        "Kursus Cowork awam mengajar kurikulum tetap menggunakan contoh orang lain. Kami menjalankan sesi persendirian berdasarkan aliran kerja sebenar anda — corak arahan yang akan digunakan semula oleh pasukan anda, dan panduan penggunaan selamat mengikut PDPA atau PDPC yang berkenaan.",
      points: [
        "Sesi persendirian berdasarkan aliran kerja anda, bukan kelas awam",
        "Disampaikan di Malaysia dan Singapura, di tapak atau dalam talian",
        "Panduan penggunaan selamat mengikut PDPA atau PDPC",
      ],
      cta: "Hubungi kami tentang latihan tersuai",
    },
    agents: {
      eyebrow: "Di mana Cowork berhenti",
      title: "Bina ejen Claude tersuai",
      body:
        "Cowork ialah pengguna tunggal dan terputus daripada sistem anda. Untuk automasi yang berjalan merentasi seluruh pasukan dan bersambung ke CRM, ERP, atau pangkalan data anda, kami mereka bentuk dan membina ejen Claude tersuai dengan Agent SDK dan MCP.",
      points: [
        "Berjalan merentasi seluruh pasukan anda, sentiasa aktif",
        "Bersambung ke sistem perniagaan anda melalui MCP",
        "Melakukan kerja berbilang langkah yang Cowork tidak boleh",
      ],
      ctaPrimary: "Rangka ejen tersuai",
      ctaSecondary: "AI Solutions Kami",
    },
  },
  faqSection: {
    eyebrow: "Soalan lazim",
  },
  faqs: [
    {
      question: "Apa itu Claude Cowork?",
      answer:
        "Claude Cowork ialah AI agentik Anthropic untuk kerja pengetahuan. Ia berjalan dalam aplikasi desktop Claude dan bekerja secara langsung dengan fail, folder, dan aplikasi tempatan anda. Sejak 16 September 2026, Cowork dan chat ialah satu Claude, jadi ini satu cara bekerja dan bukan aplikasi berasingan: anda menerangkan tugas, Claude merancang dan melaksanakannya merentasi berbilang fail dan langkah, dan anda mengemudinya sepanjang perjalanan. Ia menggunakan seni bina agentik yang sama yang menggerakkan Claude Code, tetapi tanpa memerlukan terminal.",
    },
    {
      question: "Adakah Claude Cowork percuma, dan pelan mana yang saya perlukan?",
      answer:
        "Cowork disertakan pada setiap pelan Claude berbayar — Pro, Max, Team, dan Enterprise — tetapi tidak pada pelan Free. Peringkat lebih tinggi terutamanya menaikkan berapa banyak anda boleh menggunakannya sebelum mencapai had, jadi jika anda menjalankan Cowork setiap hari atau mengikut jadual, jangkakan anda memerlukan peringkat lebih tinggi seperti Max. Sahkan pelan dan harga semasa di claude.com/pricing.",
    },
    {
      question: "Adakah Claude Cowork menghantar fail saya ke awan?",
      answer:
        "Fail anda kekal pada mesin anda. Cowork berjalan dalam mesin maya terpencil pada komputer anda sendiri dengan akses fail dan rangkaian yang terkawal, dan ia hanya menyentuh folder yang anda benarkan secara jelas. Dokumen dan data anda tidak dimuat naik untuk latihan atau storan awan. Sambungan internet masih diperlukan kerana penaakulan model berjalan pada pelayan Anthropic, tetapi fail tempatan anda tidak dihantar ke sana secara pukal.",
    },
    {
      question: "Apakah perbezaan antara Claude Cowork dan Claude Code?",
      answer:
        "Kedua-duanya menggunakan ejen asas yang sama, tetapi ia menyasarkan kerja yang berbeza. Claude Code untuk pembangun: ia berjalan dalam terminal, bekerja dalam repositori kod, dan outputnya ialah perisian. Claude Cowork untuk pekerja pengetahuan: ia berjalan dalam aplikasi desktop tanpa terminal, bekerja merentasi dokumen, hamparan, dan aplikasi harian, dan outputnya ialah kerja siap — laporan, fail tersusun, data yang diekstrak. Gunakan Code apabila outputnya ialah perisian; gunakan Cowork apabila outputnya ialah kerja pengetahuan.",
    },
    {
      question: "Apa yang Claude Cowork sebenarnya boleh lakukan?",
      answer:
        "Cowork dibina untuk tugas yang terlalu besar untuk tetingkap chat. Kerja tipikal: membaca berpuluh-puluh fail dalam folder dan menghasilkan ringkasan berstruktur; mengekstrak dan menyelaraskan data merentasi banyak dokumen; menyediakan laporan dan ringkasan; menyusun dan menamakan semula fail mengikut kandungannya; menyeragamkan set dokumen; dan menjalankan aliran kerja berbilang langkah yang boleh diulang merentasi aplikasi anda. Ia mengendalikan tugas secara menyeluruh dan bukannya menjawab satu soalan pada satu masa.",
    },
    {
      question: "Apakah batasan Claude Cowork?",
      answer:
        "Cowork ialah pengguna tunggal dan desktop sahaja (tiada web atau mudah alih), dan ia memerlukan pelan berbayar serta sambungan internet. Ingatan berfungsi dalam projek, tetapi ia tidak mengekalkan ingatan merentasi sesi tersendiri, dan aplikasi desktop mesti kekal terbuka — menutupnya menghentikan tugas yang sedang berjalan. Tugas berbilang langkah yang panjang menggunakan kuota pelan jauh lebih pantas daripada chat biasa. Penyambung luaran (seperti Gmail atau Google Drive) boleh menjadi tidak konsisten, dan — patut diberi perhatian untuk pasukan yang dikawal selia — aktiviti Cowork tidak ditangkap dalam Compliance API Anthropic pada masa ini. Kerana ia bertindak ke atas fail sebenar, semak rancangannya sebelum membenarkannya berjalan pada folder penting.",
    },
    {
      question: "Adakah Claude Cowork berjalan pada Windows, atau hanya Mac?",
      answer:
        "Cowork berjalan melalui aplikasi desktop Claude pada macOS, Windows, dan Linux (beta) — ia tidak tersedia di web atau pada mudah alih. Daripada menerbitkan spesifikasi perkakasan tetap, aplikasi Anthropic menjalankan semakan kesediaan pantas untuk mengesahkan komputer anda mampu menjalankannya. Kerana ketersediaan dan keperluan sistem berubah dengan pantas untuk produk baharu, sahkan senarai semasa di Help Center Anthropic sebelum anda merancang pelancaran.",
    },
    {
      question: "Cowork ialah pengguna tunggal — bagaimana kami mengautomasikan kerja merentasi seluruh pasukan?",
      answer:
        "Ini ialah sempadan Cowork, dan ia satu yang penting. Cowork ialah ejen desktop peribadi: ia membantu seorang individu menyelesaikan kerja pengetahuannya sendiri. Ia tidak menjalankan automasi seluruh pasukan, bersambung ke CRM atau ERP anda, atau beroperasi sebagai perkhidmatan sentiasa aktif yang dikongsi. Apabila anda memerlukan automasi yang merentasi seluruh pasukan dan bersambung ke sistem perniagaan anda, itu ialah ejen Claude tersuai — dibina dengan Claude Agent SDK dan Model Context Protocol (MCP). Anchor Sprint, diperakui dalam Claude Partner Network, membina tepat itu, dan juga boleh melatih pasukan anda untuk mendapatkan lebih banyak daripada Cowork itu sendiri.",
    },
  ],
  keepReading: {
    eyebrow: "Teruskan membaca",
    links: [
      "Bandingkan setiap pelan Claude (Free hingga Enterprise)",
      "Privasi & pematuhan Claude",
      "Perkongsian Anthropic Claude kami",
      "Ejen AI tersuai yang kami bina",
    ],
  },
  disclaimer: {
    label: "Penafian",
    before:
      "Kami menyusun panduan ini daripada laman produk rasmi dan Help Center Anthropic untuk memberi anda satu rujukan yang jelas dan bersatu untuk Claude Cowork. Cowork adalah baharu dan berubah dengan pantas, jadi walaupun usaha terbaik kami untuk memastikan ini tepat (disahkan kali terakhir pada September 2026), sesetengah butiran mungkin sudah lapuk. Untuk maklumat terkini dan sahih, sentiasa rujuk ",
    linkLabel: "laman web rasmi Anthropic",
    after:
      " — tiada apa-apa di sini menggantikan dokumentasi Anthropic sendiri. Anchor Sprint membina ejen Claude tersuai dan menyediakan pemerkasaan, dan bukan penjual semula Claude. Claude and Anthropic are trademarks of Anthropic, PBC. Anchor Sprint is Certified in the Claude Partner Network; ini tidak membayangkan sokongan terhadap perkhidmatan kami.",
  },
  consultation: {
    title: "Cowork memulakan anda — sedia untuk mengautomasikan yang selebihnya?",
    description:
      "Beritahu kami kerja berulang dan berbilang langkah yang dilakukan pasukan anda merentasi sistemnya. Kami akan tunjukkan apa yang boleh diambil alih oleh ejen Claude tersuai, dan membantu pasukan anda mendapatkan lebih banyak daripada Cowork sepanjang perjalanan.",
    buttonText: "Hubungi Pasukan Kami",
  },
};

const zh: Content = {
  mergeNotice: {
    heading: "2026 年 9 月 22 日更新——Cowork 与 chat 现已合并为一个 Claude",
    body:
      "2026 年 9 月 16 日，Anthropic 将 Claude Cowork 与 Claude chat 合并为一个 Claude。您不再需要判断某项任务属于哪里：在同一个地方提出简短问题或交付一整份报告，而且在您合上笔记本电脑之后，Claude 仍可继续工作。该功能先在 Pro 与 Max 方案上跨网页、桌面与移动端推出，Team 与 Free 方案随后跟进——无需开启任何设置；若您此前在 Cowork 中有工作内容，它仍在原处。Cowork 并未被取消，Anthropic 仍在使用这个名称。请将以下指南视为对这种工作方式的说明；其中提到 Cowork 是一个独立场所之处，描述的是此次合并之前的情况。",
    linkLabel: "Anthropic 的公告",
  },
  hero: {
    breadcrumbHome: "首页",
    breadcrumbCurrent: "Claude Cowork",
    eyebrow: "完整指南",
    title: "什么是 Claude Cowork——以及如何使用它",
    introBefore:
      "Claude Cowork 是 Anthropic 面向知识工作的智能代理式 AI：它运行于 Claude 桌面应用中，跨越您的本地文件、文件夹和应用程序完成整项任务。本指南涵盖它是什么、它解决哪些问题、如何使用它，以及——坦诚地说——它在哪里止步、定制 AI 代理从何处接手。由",
    introLinkLabel: "马来西亚的 Claude 合作伙伴",
    introAfter: "为马来西亚团队撰写。",
    ctaPrimary: "超越 Cowork 实现自动化——联系我们",
    ctaSecondary: "Cowork vs Claude Code vs chat",
    disclaimerBefore:
      "独立汇编的参考资料——最后核实于 2026 年 9 月。Cowork 变化迅速；如需最新信息，请始终查阅 ",
    disclaimerLinkLabel: "Anthropic 官方网站",
    disclaimerAfter: "。",
    imageAlt:
      "抽象的编辑插画：左侧纠缠的墨线在右侧化为平静、有序的平行线条，散落的方块汇成整齐的一排——Claude Cowork 为散乱的工作带来秩序。",
  },
  whatIs: {
    eyebrow: "什么是 Claude Cowork？",
    p1Emphasis: "Cowork 是一种工作方式，而不是一个独立的应用。",
    p1Rest:
      " 当 Claude chat 一次回答一条消息时，Cowork 承担整项任务：您描述期望的结果，Claude 规划步骤并跨越您的文件执行它们，而您在过程中把控方向。",
    p2a: "它存在于 ",
    p2Emphasis1: "Claude 桌面应用",
    p2b:
      "之中——无需终端、无需设置脚本——并采用与 Claude Code 相同的智能代理式架构。它运行于 ",
    p2Emphasis2: "您自己电脑上的隔离虚拟机",
    p2c:
      "中，具备受控的文件和网络访问权限，因此它可以对真实文档进行操作，同时您的数据保持在本地。",
    cards: [
      { label: "运行于您的桌面", note: "在 Claude 应用中，无需终端" },
      { label: "文件保持在本地", note: "沙盒虚拟机，您授权的文件夹" },
      { label: "完成整项任务", note: "规划、执行，您把控方向" },
    ],
  },
  video: {
    eyebrow: "观看实际运行",
    title: "Anthropic 介绍 Cowork",
    body:
      "与其听我们说，不如观看 Cowork 背后的团队亲自演示。这是 Anthropic 的官方介绍——了解一次工作会话实际感受的最快方式。",
    iframeTitle: "Anthropic — The Future of AI at Work: Introducing Cowork",
    playAriaLabel: "播放视频：Anthropic 介绍 Claude Cowork",
    facadeImgAlt: "Anthropic 对 Claude Cowork 的官方介绍",
    creditBefore:
      "视频由 Anthropic 制作，嵌入自 YouTube。",
    creditLinkLabel: "在 YouTube 上观看",
    creditAfter: "。",
  },
  problemsSection: {
    eyebrow: "它解决什么问题？",
  },
  problems: [
    {
      title: "对聊天窗口而言过于庞大的工作",
      body:
        "聊天一次处理一个问题。Cowork 读取数十个文件，产出大量输出，并持续推进一项多步骤任务，无需您反复粘贴上下文。",
    },
    {
      title: "散乱的文件和手动的繁琐工作",
      body:
        "跨文档集合进行排序、重命名、标准化和提取数据，若手动操作既缓慢又容易出错。Cowork 在一次工作会话中跨越整个文件夹完成这些工作。",
    },
    {
      title: "无人有时间处理的可重复工作流程",
      body:
        "定期报告、每周清理、数据提取。Cowork 可按需再次运行同一套多步骤例程，让您的团队腾出精力从事需要判断的工作。",
    },
  ],
  compare: {
    eyebrow: "Cowork vs Claude Code vs chat",
    title: "您应该使用哪一个？",
    body:
      "三者共享同样的 Claude 智能——它们的区别在于运行的位置以及产出的内容。点按一个工具以高亮它，看看它适合哪些日常任务。",
    showAll: "显示全部",
    dimensionHeader: "维度",
    taskRouterLabel: "为任务匹配工具",
  },
  tools: [
    { id: "chat", label: "Claude chat", bestFor: "在对话中进行快速提问、起草和边想边说。" },
    { id: "cowork", label: "Claude Cowork", bestFor: "跨越您的本地文件、文件夹和应用程序完成知识工作。" },
    { id: "code", label: "Claude Code", bestFor: "从终端在代码仓库中构建软件。" },
  ],
  comparison: [
    { dimension: "最适合", chat: "快速解答与起草", cowork: "日常知识工作", code: "编写与交付软件" },
    { dimension: "运行位置", chat: "网页、移动端与桌面", cowork: "桌面应用（无终端）", code: "终端 / IDE" },
    { dimension: "与您的文件协作", chat: "仅上传", cowork: "您授权的本地文件夹", code: "您的代码仓库" },
    { dimension: "工作方式", chat: "一次一轮", cowork: "规划并运行整项任务", code: "规划并运行整项任务" },
    { dimension: "典型产出", chat: "一条回复", cowork: "报告、整理好的文件、数据", code: "代码、脚本、修复" },
    { dimension: "适用对象", chat: "所有人", cowork: "运营、财务、法务、研究", code: "开发者" },
    { dimension: "隔离 / 安全", chat: "不适用", cowork: "沙盒虚拟机、授权文件夹", code: "在您的环境中运行" },
  ],
  taskRoutes: [
    { task: "将一个含 40 份会议记录的文件夹汇总成一份简报", tool: "cowork" },
    { task: "就某条合同条款提一个一次性问题", tool: "chat" },
    { task: "跨十几个电子表格核对数字", tool: "cowork" },
    { task: "起草一封快速电子邮件或社交帖子", tool: "chat" },
    { task: "构建一个内部自动化脚本", tool: "code" },
    { task: "根据内容重命名并整理 200 个文件", tool: "cowork" },
    { task: "调试代码库中一个失败的测试", tool: "code" },
    { task: "为一场活动进行头脑风暴", tool: "chat" },
    { task: "从原始导出数据准备月度运营报告", tool: "cowork" },
  ],
  howTo: {
    eyebrow: "如何使用 Claude Cowork",
    steps: [
      {
        title: "安装 Claude 桌面应用",
        body:
          "从 claude.com/download 下载 Claude Desktop 并登录。Cowork 需要付费方案（Pro、Max、Team 或 Enterprise）——Free 方案不包含——以及受支持的桌面系统：Mac、Windows 或 Linux（beta）。该应用会运行一次快速就绪检查，以确认您的电脑能够运行它。",
      },
      {
        title: "打开 Cowork 并授予文件夹访问权限",
        body:
          "在桌面应用中选择 Cowork 选项卡，然后通过 Settings 添加您希望 Claude 在其中工作的特定文件夹。它只会触碰您明确授权的文件夹，且在您电脑上的隔离虚拟机内进行。",
      },
      {
        title: "描述任务，然后审阅方案",
        body:
          "告诉 Claude 您想要的结果——“将此文件夹中的每个文件汇总为一份带有负责人和日期的简报”——而不是逐步的指令。Claude 会提出一种方案；在它运行之前审阅它，尤其是针对重要文件夹时。",
      },
      {
        title: "让它运行，并在过程中把控方向",
        body:
          "Cowork 跨越文件和步骤执行，同时您进行观察和纠正。当它完成时，像审阅初级同事的工作那样审阅其输出——它快速且能干，但最终的把关由您负责。",
      },
    ],
  },
  useCasesSection: {
    eyebrow: "面向马来西亚团队的真实用例",
    title: "周一早上可以交给 Cowork 的工作",
  },
  useCases: [
    {
      tag: "运营",
      title: "将原始导出数据转化为月度报告",
      body:
        "将 Cowork 指向一个含 CSV 导出文件和上月报告模板的文件夹；它核对数字并起草本月版本。",
      prompt:
        "“阅读 /ops-exports 中的每一个 CSV，套用 last-month-report.docx 的格式，并起草本月的运营报告，标注出差异项。”",
    },
    {
      tag: "财务",
      title: "核对发票并标记不一致之处",
      body:
        "交给它一个含发票的文件夹和一份账簿；它提取总额、进行匹配，并列出无法核对的项目——附带引用。",
      prompt:
        "“将 /invoices 中的发票与 ledger.xlsx 进行比对，并生成一份不一致清单，附上发票号码和金额。”",
    },
    {
      tag: "法务与行政",
      title: "汇总一组文档以供审阅",
      body:
        "给它一个含合同的目录；它生成一份结构化摘要——各方、关键日期、义务——让您的团队更快审阅。",
      prompt:
        "“汇总 /vendor-agreements 中的每一份合同：各方、续约日期、通知期，以及任何自动续约条款，以表格形式呈现。”",
    },
    {
      tag: "市场营销与研究",
      title: "将研究综合成一份简报",
      body:
        "把笔记、PDF 和文字记录交给它；它将主题提炼成一份单一简报，附有可供您采取行动的来源。",
      prompt:
        "“阅读 /market-research 中的所有内容，就前三大主题撰写一份单页简报，并为每个要点注明来源文件。”",
    },
  ],
  pricing: {
    eyebrow: "方案、定价与用量",
    p1a: "Cowork ",
    p1Emphasis1: "包含在每个付费的 Claude 方案中",
    p1b:
      "——Pro、Max、Team 和 Enterprise——但 Free 不包含。重要的是，Pro 并非精简版：您获得完整的 Cowork 功能集。各层级之间的区别在于 ",
    p1Emphasis2: "您能使用它多少",
    p1c: "才会触及上限。",
    p2a:
      "由于 Cowork 运行冗长的多步骤任务，它消耗用量的速度远快于普通聊天。在您开始每天或按计划运行 Cowork 之前，Pro 确实足够；一旦如此，Max 5× 是现实的下限，而 Max 20× 则用于让它整天持续工作。如需逐个方案的完整对比，请参阅我们的 ",
    p2LinkLabel1: "马来西亚 Claude 方案对比",
    p2b: "；如需了解各方案的隐私与数据处理，请参阅我们的 ",
    p2LinkLabel2: "Claude 隐私与合规指南",
    p2c: "。",
  },
  limitationsSection: {
    eyebrow: "坦诚的局限",
    title: "Claude Cowork（目前）无法做到的事",
    body:
      "Cowork 很强大，但了解它的边界才是让它真正有用的关键——它也指明了您在何处需要更进一步的东西。",
  },
  limitations: [
    "单用户且仅限桌面：无网页、无移动端、无共享会话。它帮助一个人完成自己的工作。",
    "基于会话的记忆：记忆在项目内有效，但 Cowork 不会跨独立会话保留记忆。",
    "桌面应用必须保持打开：如果您关闭 Claude Desktop，任何正在运行的任务都会停止。",
    "用量消耗大：冗长的多步骤任务消耗方案用量的速度远快于普通聊天，因此重度使用可能很快触及您的上限。",
    "连接器可靠性参差不齐：与 Gmail 或 Google Drive 等工具的集成目前可能不稳定。",
    "对受监管团队存在治理缺口：Cowork 的活动目前未被纳入 Anthropic 的 Compliance API。",
    "需要付费方案 + 互联网：Cowork 不包含在 Free 中，且在整个会话期间需要有效的连接。",
    "把关由您负责：在永久删除文件之前它会请求批准，但由于它对真实文档进行操作，请始终先审阅其方案。",
  ],
  conversion: {
    eyebrow: "接下来去往何处",
    title: "我们借助 Claude 带您的团队更进一步的两种方式",
    body:
      "无论您的团队只是需要熟练掌握 Cowork，还是需要 Cowork 无法实现的自动化，这都是我们作为 Claude Partner Network 认证合作伙伴的工作。",
    training: {
      eyebrow: "定制培训",
      title: "为马来西亚与新加坡团队提供的 Cowork 定制培训",
      body:
        "公开的 Cowork 课程教授固定课纲，用的是别人的案例。我们提供私密课程，完全基于您真实的工作流程——包括您的团队隔天就能复用的提示模式，以及依 PDPA 或 PDPC 规范制定的安全使用指引。",
      points: [
        "基于您工作流程的私密课程，而非公开班",
        "在马来西亚与新加坡交付，可到场或线上进行",
        "依 PDPA 或 PDPC 制定的安全使用指引",
      ],
      cta: "咨询定制培训",
    },
    agents: {
      eyebrow: "Cowork 止步之处",
      title: "构建定制的 Claude 代理",
      body:
        "Cowork 是单用户的，且与您的系统相互隔绝。对于跨越整个团队运行并接入您的 CRM、ERP 或数据库的自动化，我们借助 Agent SDK 和 MCP 设计并构建定制的 Claude 代理。",
      points: [
        "跨越您的整个团队运行，始终在线",
        "通过 MCP 接入您的业务系统",
        "完成 Cowork 无法完成的多步骤工作",
      ],
      ctaPrimary: "规划一个定制代理",
      ctaSecondary: "我们的 AI Solutions",
    },
  },
  faqSection: {
    eyebrow: "常见问题",
  },
  faqs: [
    {
      question: "什么是 Claude Cowork？",
      answer:
        "Claude Cowork 是 Anthropic 面向知识工作的智能代理式 AI。它运行于 Claude 桌面应用中，直接与您的本地文件、文件夹和应用程序协作。自 2026 年 9 月 16 日起，Cowork 与 chat 已合并为一个 Claude，因此这是一种工作方式，而非独立的应用：您描述一项任务，Claude 跨越多个文件和步骤进行规划和执行，而您在过程中把控方向。它采用与 Claude Code 相同的智能代理式架构，但无需终端。",
    },
    {
      question: "Claude Cowork 是免费的吗，我需要哪个方案？",
      answer:
        "Cowork 包含在每个付费的 Claude 方案中——Pro、Max、Team 和 Enterprise——但不包含在 Free 方案中。更高层级主要是提高您在触及上限之前能使用它多少，因此如果您每天或按计划运行 Cowork，预计需要更高层级，例如 Max。请在 claude.com/pricing 核实当前的方案和定价。",
    },
    {
      question: "Claude Cowork 会把我的文件发送到云端吗？",
      answer:
        "您的文件保留在您的机器上。Cowork 运行于您自己电脑上的隔离虚拟机中，具备受控的文件和网络访问权限，并且它只触碰您明确授权的文件夹。您的文档和数据不会被上传用于训练或云端存储。仍需互联网连接，因为模型推理运行在 Anthropic 的服务器上，但您的本地文件不会被整体发送到那里。",
    },
    {
      question: "Claude Cowork 与 Claude Code 有什么区别？",
      answer:
        "两者使用相同的底层代理，但针对不同的工作。Claude Code 面向开发者：它运行于终端，在代码仓库内工作，其产出是软件。Claude Cowork 面向知识工作者：它运行于桌面应用中，无需终端，跨越文档、电子表格和日常应用工作，其产出是完成的工作——报告、整理好的文件、提取的数据。当产出是软件时，选择 Code；当产出是知识工作时，选择 Cowork。",
    },
    {
      question: "Claude Cowork 实际上能做什么？",
      answer:
        "Cowork 专为对聊天窗口而言过于庞大的任务而打造。典型工作包括：读取文件夹中的数十个文件并生成结构化摘要；跨众多文档提取并核对数据；准备报告和简报；根据内容排序和重命名文件；标准化文档集合；以及跨您的应用程序运行可重复的多步骤工作流程。它处理整项任务，而不是一次回答一个问题。",
    },
    {
      question: "Claude Cowork 有哪些局限？",
      answer:
        "Cowork 是单用户且仅限桌面（无网页或移动端），且需要付费方案外加互联网连接。记忆在项目内有效，但它不会跨独立会话保留记忆，而且桌面应用必须保持打开——关闭它会停止正在运行的任务。冗长的多步骤任务消耗方案用量的速度远快于普通聊天。外部连接器（如 Gmail 或 Google Drive）可能不稳定，并且——对受监管团队而言值得注意——Cowork 的活动目前未被纳入 Anthropic 的 Compliance API。由于它对真实文件进行操作，在让它对重要文件夹运行之前请审阅其方案。",
    },
    {
      question: "Claude Cowork 能在 Windows 上运行，还是只能在 Mac 上？",
      answer:
        "Cowork 通过 Claude 桌面应用在 macOS、Windows 和 Linux（beta）上运行——它不在网页或移动端提供。Anthropic 的应用并未发布固定的硬件规格，而是运行一次快速就绪检查，以确认您的电脑能够运行它。由于新产品的可用性和系统要求变化迅速，在您规划推行之前，请在 Anthropic 的 Help Center 确认当前列表。",
    },
    {
      question: "Cowork 是单用户的——我们如何跨越整个团队实现工作自动化？",
      answer:
        "这是 Cowork 的边界，而且是一个重要的边界。Cowork 是一个个人桌面代理：它帮助一位个人完成自己的知识工作。它不运行团队范围的自动化，不接入您的 CRM 或 ERP，也不作为共享的始终在线服务运作。当您需要跨越整个团队并接入您业务系统的自动化时，那就是定制的 Claude 代理——借助 Claude Agent SDK 和 Model Context Protocol (MCP) 构建。Anchor Sprint 作为 Claude Partner Network 认证合作伙伴，正是构建这类系统，同时也能培训您的团队，从 Cowork 本身获得更多价值。",
    },
  ],
  keepReading: {
    eyebrow: "继续阅读",
    links: [
      "对比每一个 Claude 方案（Free 到 Enterprise）",
      "Claude 隐私与合规",
      "我们的 Anthropic Claude 合作关系",
      "我们构建的定制 AI 代理",
    ],
  },
  disclaimer: {
    label: "免责声明",
    before:
      "我们根据 Anthropic 的官方产品页面和 Help Center 汇编本指南，为您提供一份关于 Claude Cowork 的清晰、统一的参考。Cowork 是新产品且变化迅速，因此尽管我们尽力保持其准确（最后核实于 2026 年 9 月），部分细节可能已过时。如需最新且权威的信息，请始终参阅 ",
    linkLabel: "Anthropic 官方网站",
    after:
      "——此处的任何内容都不能取代 Anthropic 自身的文档。Anchor Sprint 构建定制的 Claude 代理并提供赋能，而非 Claude 的转售商。Claude and Anthropic are trademarks of Anthropic, PBC. Anchor Sprint is Certified in the Claude Partner Network；这并不意味着我们的服务获得背书。",
  },
  consultation: {
    title: "Cowork 让您起步——准备好将其余的工作自动化了吗？",
    description:
      "告诉我们您的团队在其各个系统中所做的重复性、多步骤的工作。我们将向您展示定制的 Claude 代理能为他们分担哪些工作，并在过程中帮助您的团队从 Cowork 获得更多价值。",
    buttonText: "联系我们的团队",
  },
};

export function getClaudeCoworkContent(locale: Locale): Content {
  return locale === "ms" ? ms : locale === "zh" ? zh : en;
}
